import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  IconButton
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import dynamicContentService from '@/services/dynamicContentService';

interface EmergencyContact {
  id: string;
  name: string;
  description: string;
  phoneNumber: string;
  countryCode: string;
  serviceType: string;
  is247: boolean;
  icon: string;
  displayOrder: number;
  isActive: boolean;
}

const EmergencyContactsPage: React.FC = () => {
  const [contacts, setContacts] = useState<EmergencyContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingContact, setEditingContact] = useState<EmergencyContact | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    phone_number: '',
    country_code: 'NG',
    service_type: 'emergency',
    is_24_7: true,
    icon: '🚨',
    display_order: 0
  });

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await dynamicContentService.getEmergencyContacts();

      if (result.success) {
        setContacts(result.data);
      } else {
        setError(result.error || 'Failed to fetch emergency contacts');
      }
    } catch (err: any) {
      console.error('Error fetching emergency contacts:', err);
      setError(err.response?.data?.error || 'Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (contact?: EmergencyContact) => {
    if (contact) {
      setEditingContact(contact);
      setFormData({
        name: contact.name,
        description: contact.description,
        phone_number: contact.phoneNumber,
        country_code: contact.countryCode,
        service_type: contact.serviceType,
        is_24_7: contact.is247,
        icon: contact.icon,
        display_order: contact.displayOrder
      });
    } else {
      setEditingContact(null);
      setFormData({
        name: '',
        description: '',
        phone_number: '',
        country_code: 'NG',
        service_type: 'emergency',
        is_24_7: true,
        icon: '🚨',
        display_order: 0
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingContact(null);
  };

  const handleSave = async () => {
    setError(null);

    try {
      const result = editingContact
        ? await dynamicContentService.updateEmergencyContact(editingContact.id, formData)
        : await dynamicContentService.createEmergencyContact(formData);

      if (result.success) {
        setSuccess(editingContact ? 'Contact updated successfully' : 'Contact created successfully');
        setTimeout(() => setSuccess(null), 3000);
        handleCloseDialog();
        fetchContacts();
      } else {
        setError(result.error || 'Failed to save contact');
      }
    } catch (err: any) {
      console.error('Error saving contact:', err);
      setError(err.response?.data?.error || 'Network error. Please try again.');
    }
  };

  const handleDelete = async (contactId: string) => {
    if (!confirm('Are you sure you want to delete this emergency contact?')) return;

    try {
      const result = await dynamicContentService.deleteEmergencyContact(contactId);

      if (result.success) {
        setSuccess('Contact deleted successfully');
        setTimeout(() => setSuccess(null), 3000);
        fetchContacts();
      } else {
        setError(result.error || 'Failed to delete contact');
      }
    } catch (err: any) {
      console.error('Error deleting contact:', err);
      setError(err.response?.data?.error || 'Network error. Please try again.');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Emergency Contacts
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Contact
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Icon</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Phone Number</TableCell>
              <TableCell>Country</TableCell>
              <TableCell>Service Type</TableCell>
              <TableCell>24/7</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contacts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No emergency contacts found
                </TableCell>
              </TableRow>
            ) : (
              contacts.map((contact) => (
                <TableRow key={contact.id}>
                  <TableCell>{contact.icon}</TableCell>
                  <TableCell>{contact.name}</TableCell>
                  <TableCell>{contact.phoneNumber}</TableCell>
                  <TableCell>{contact.countryCode}</TableCell>
                  <TableCell>
                    <Chip
                      label={contact.serviceType}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {contact.is247 ? '✓' : '✗'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={contact.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={contact.isActive ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleOpenDialog(contact)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(contact.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingContact ? 'Edit Emergency Contact' : 'Add Emergency Contact'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              fullWidth
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />

            <TextField
              fullWidth
              label="Description"
              multiline
              rows={2}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />

            <TextField
              fullWidth
              label="Phone Number"
              value={formData.phone_number}
              onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
            />

            <FormControl fullWidth>
              <InputLabel>Country Code</InputLabel>
              <Select
                value={formData.country_code}
                label="Country Code"
                onChange={(e) => setFormData({ ...formData, country_code: e.target.value })}
              >
                <MenuItem value="NG">Nigeria (NG)</MenuItem>
                <MenuItem value="US">United States (US)</MenuItem>
                <MenuItem value="GB">United Kingdom (GB)</MenuItem>
                <MenuItem value="CA">Canada (CA)</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Service Type</InputLabel>
              <Select
                value={formData.service_type}
                label="Service Type"
                onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
              >
                <MenuItem value="emergency">Emergency</MenuItem>
                <MenuItem value="police">Police</MenuItem>
                <MenuItem value="fire">Fire</MenuItem>
                <MenuItem value="medical">Medical</MenuItem>
                <MenuItem value="roadside">Roadside Assistance</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Icon (emoji)"
              value={formData.icon}
              onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
            />

            <TextField
              fullWidth
              label="Display Order"
              type="number"
              value={formData.display_order}
              onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_24_7}
                  onChange={(e) => setFormData({ ...formData, is_24_7: e.target.checked })}
                />
              }
              label="Available 24/7"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            {editingContact ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmergencyContactsPage;
